import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useSettings } from '../../lib/settings';
import { Icons } from '../ui/Icons';

const NAV = [
  { label: 'How it works', href: '#how' },
  { label: 'Why Rosa', href: '#why' },
  { label: 'The bathroom', href: '#explore' },
  { label: 'For families', href: '#families' },
  { label: 'Questions', href: '#faq' },
];

export function Header() {
  const { openLeadForm } = useApp();
  const settings = useSettings();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <>
      {settings.announcement_enabled && settings.announcement_text && (
        <div className="relative z-40 bg-plum text-blush">
          <div className="container-rosa flex items-center justify-center gap-2 py-2 text-center">
            <Icons.sparkle width={15} height={15} className="shrink-0 text-terracotta-soft" />
            <p className="font-body text-[13px] font-medium tracking-wide">{settings.announcement_text}</p>
          </div>
        </div>
      )}

      <header
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrolled ? 'border-b border-stone/70 bg-blush/85 backdrop-blur-lg shadow-soft' : 'bg-transparent'
        }`}
      >
        <div className="container-rosa flex items-center justify-between py-3.5">
          <a href="#top" className="group flex items-center gap-2" aria-label="Rosa home">
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-terracotta/10 font-display text-2xl font-extrabold text-terracotta transition-transform group-hover:scale-105">
              R
            </span>
            <span className="font-display text-2xl font-bold tracking-tight text-plum">Rosa</span>
          </a>

          <nav className="hidden items-center gap-7 lg:flex">
            {NAV.map((n) => (
              <a key={n.href} href={n.href} className="link-underline font-body text-[15px] font-medium text-plum/80">
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button onClick={() => openLeadForm()} className="btn-primary hidden !py-3 !text-[15px] sm:inline-flex">
              {settings.primary_cta_label}
            </button>
            <button
              onClick={() => setMenuOpen((o) => !o)}
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              className="grid h-11 w-11 cursor-pointer place-items-center rounded-xl border border-stone bg-white/70 text-plum lg:hidden"
            >
              {menuOpen ? <Icons.close width={22} height={22} /> : <MenuBars />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 lg:hidden"
          >
            <div className="absolute inset-0 bg-plum/40 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
            <motion.nav
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 34 }}
              className="absolute right-0 top-0 flex h-full w-[82%] max-w-sm flex-col bg-blush p-6 shadow-lift"
            >
              <div className="mb-6 flex items-center justify-between">
                <span className="font-display text-2xl font-bold text-plum">Rosa</span>
                <button
                  onClick={() => setMenuOpen(false)}
                  aria-label="Close menu"
                  className="grid h-10 w-10 cursor-pointer place-items-center rounded-xl border border-stone text-plum"
                >
                  <Icons.close width={22} height={22} />
                </button>
              </div>
              <div className="flex flex-col gap-1">
                {NAV.map((n) => (
                  <a
                    key={n.href}
                    href={n.href}
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center justify-between rounded-2xl px-4 py-4 font-display text-xl font-medium text-plum transition-colors hover:bg-mist"
                  >
                    {n.label}
                    <Icons.chevronRight width={20} height={20} className="text-plum/30" />
                  </a>
                ))}
              </div>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  openLeadForm();
                }}
                className="btn-primary mt-auto w-full"
              >
                {settings.primary_cta_label}
              </button>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function MenuBars() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  );
}
